/**
 * Notes and the lead activity timeline.
 * Props follow the follow-ups page: api is API.json (method, url, body).
 */
const { useState, useEffect } = React;

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function safeZone(zone) {
  const name = zone || 'America/Los_Angeles';
  try {
    Intl.DateTimeFormat('en-US', { timeZone: name }).format(new Date());
    return name;
  } catch (_) {
    return 'America/Los_Angeles';
  }
}

function ymdInZone(date, timeZone) {
  const map = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date).forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  return map.year + '-' + map.month + '-' + map.day;
}

function addYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function formatStamp(iso, timeZone) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const map = {};
  try {
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }).formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') map[part.type] = part.value;
    });
  } catch (_) {
    return '';
  }
  if (!map.month) return '';
  return map.month + ' ' + map.day + ', ' + map.hour + ':' + (map.minute || '00') + ' ' + (map.dayPeriod || '') + ' ' + (map.timeZoneName || '');
}

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 0) return '';
  if (sec < 45) return 'Just now';
  const min = Math.round(sec / 60);
  if (min < 60) return min + 'm ago';
  const hr = Math.round(min / 60);
  if (hr < 24) return hr + 'h ago';
  const day = Math.round(hr / 24);
  if (day < 7) return day + 'd ago';
  return '';
}

function dayLabel(iso, timeZone) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    const key = ymdInZone(date, timeZone);
    const today = ymdInZone(new Date(), timeZone);
    if (key === today) return 'Today';
    if (key === addYmd(today, -1)) return 'Yesterday';
    return new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    }).format(date);
  } catch (_) {
    return '';
  }
}

function canChange(note, user) {
  if (!user || !note) return false;
  const role = String(user.role || '');
  if (role === 'super_admin' || role === 'tenant_admin' || role === 'supervisor') return true;
  return note.author_user_id != null && String(note.author_user_id) === String(user.id);
}

function authorLabel(note) {
  if (note.author_name) return note.author_name;
  if (note.source === 'import') return 'Imported';
  if (note.source === 'ai') return 'AI';
  if (note.source === 'agent_live') return 'Agent';
  return 'Teammate';
}

function TimelineIcon({ type }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' };
  let body = null;
  if (type === 'call') {
    body = <path d="M7 4h3l1 3-2 1a12 12 0 0 0 6 6l1-2 3 1v3a2 2 0 0 1-2 2A16 16 0 0 1 5 6a2 2 0 0 1 2-2z" />;
  } else if (type === 'follow_up' || type === 'callback_request') {
    body = <><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></>;
  } else if (type === 'status_change') {
    body = <path d="M5 12h14M13 6l6 6-6 6" />;
  } else if (type === 'campaign') {
    body = <path d="M4 10v4l10 4V6L4 10zm10-1 5-2v8l-5-2" />;
  } else if (type === 'crm_sync') {
    body = <path d="M20 12a8 8 0 0 1-13.5 5.5L4 16m0 0v4m0-4h4M4 12a8 8 0 0 1 13.5-5.5L20 8m0 0V4m0 4h-4" />;
  } else {
    body = <><path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M14 3v5h5M8 13h8M8 17h5" /></>;
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" {...common}>{body}</svg>
  );
}

export function NotesPanel({ api, toast, user, leadId, callSid, timezone, compact }) {
  const readOnly = !!(user && user.role === 'viewer');
  const [serverZone, setServerZone] = useState('');
  const tz = safeZone(timezone || serverZone);
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const sid = callSid != null && String(callSid).trim() !== '' ? String(callSid).trim() : '';
  const lead = leadId != null && String(leadId).trim() !== '' ? String(leadId).trim() : '';
  const ready = typeof api === 'function' && (sid || lead);

  function notify(message, type) {
    if (typeof toast === 'function') toast(message, type || 'info');
  }

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const path = sid
      ? '/api/calls/' + encodeURIComponent(sid) + '/notes'
      : '/api/leads/' + encodeURIComponent(lead) + '/notes';
    api('GET', path)
      .then((result) => {
        if (cancelled) return;
        setNotes((result && result.notes) || []);
        if (result && result.timezone) setServerZone(result.timezone);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Could not load notes.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ready, sid, lead]);

  async function reload() {
    const path = sid
      ? '/api/calls/' + encodeURIComponent(sid) + '/notes'
      : '/api/leads/' + encodeURIComponent(lead) + '/notes';
    const result = await api('GET', path);
    setNotes((result && result.notes) || []);
    if (result && result.timezone) setServerZone(result.timezone);
  }

  async function saveNew() {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    setError('');
    try {
      if (sid) await api('POST', '/api/calls/' + encodeURIComponent(sid) + '/notes', { body });
      else await api('POST', '/api/leads/' + encodeURIComponent(lead) + '/notes', { body });
      setDraft('');
      await reload();
      notify('Note saved', 'success');
    } catch (err) {
      setError(errorMessage(err, 'Could not save this note.'));
    } finally {
      setSaving(false);
    }
  }

  function onComposerKey(event) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      saveNew();
    }
  }

  async function saveEdit(note) {
    setSaving(true);
    setError('');
    try {
      await api('PATCH', '/api/notes/' + note.id, { body: editDraft });
      setEditingId(null);
      await reload();
      notify('Note updated', 'success');
    } catch (err) {
      setError(errorMessage(err, 'Could not update this note.'));
    } finally {
      setSaving(false);
    }
  }

  async function togglePin(note) {
    setSaving(true);
    setError('');
    try {
      await api('PATCH', '/api/notes/' + note.id, { pinned: !note.pinned });
      await reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not update this note.'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(note) {
    if (typeof window !== 'undefined' && !window.confirm('Delete this note?')) return;
    setSaving(true);
    setError('');
    try {
      await api('DELETE', '/api/notes/' + note.id);
      await reload();
      notify('Note deleted', 'success');
    } catch (err) {
      setError(errorMessage(err, 'Could not delete this note.'));
    } finally {
      setSaving(false);
    }
  }

  if (!ready) {
    return (
      <section className={'ln' + (compact ? ' ln--compact' : '')}>
        <p className="ln-empty">Notes for this call show up once the phone is connected to the workspace.</p>
      </section>
    );
  }

  return (
    <section className={'ln' + (compact ? ' ln--compact' : '')} aria-label="Notes">
      {error ? <div className="ln-error" role="alert">{error}</div> : null}
      {readOnly ? null : (
        <form className="ln-composer" onSubmit={(event) => { event.preventDefault(); saveNew(); }}>
          <label className="ln-sr" htmlFor={'ln-draft-' + (sid || lead)}>
            Note
          </label>
          <textarea
            id={'ln-draft-' + (sid || lead)}
            className="ln-input"
            value={draft}
            rows={compact ? 2 : 3}
            maxLength={5000}
            placeholder="Add a note"
            aria-label="Note"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKey}
          />
          <div className="ln-bar">
            <span className="ln-hint">Cmd or Ctrl + Enter to save</span>
            <button type="submit" className="ln-btn ln-btn--primary" disabled={saving || !draft.trim()}>Save note</button>
          </div>
        </form>
      )}
      {loading && !notes.length ? <p className="ln-empty">Loading notes…</p> : null}
      {!loading && !notes.length ? <p className="ln-empty">{readOnly ? 'No notes yet.' : 'No notes yet. Add one above.'}</p> : null}
      <div className="ln-list">
        {notes.map((note) => {
          const mine = canChange(note, user);
          const editing = editingId === note.id;
          const rel = relativeTime(note.created_at);
          return (
            <article key={note.id} className={'ln-note' + (note.pinned ? ' ln-note--pinned' : '')}>
              <div className="ln-meta">
                <span className="ln-who">{authorLabel(note)}</span>
                <time className="ln-time" dateTime={note.created_at || undefined}>{formatStamp(note.created_at, tz)}</time>
                {rel ? <span className="ln-rel">{rel}</span> : null}
                {note.source === 'agent_live' ? <span className="ln-badge ln-badge--live">Live call</span> : null}
                {note.hubspot_note_id ? <span className="ln-badge">Synced to HubSpot</span> : null}
              </div>
              {editing ? (
                <form className="ln-edit" onSubmit={(event) => { event.preventDefault(); saveEdit(note); }}>
                  <textarea className="ln-input" value={editDraft} maxLength={5000} aria-label="Edit note" onChange={(event) => setEditDraft(event.target.value)} />
                  <div className="ln-tools">
                    <button type="submit" className="ln-btn ln-btn--primary" disabled={saving || !editDraft.trim()}>Save</button>
                    <button type="button" className="ln-btn" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </form>
              ) : (
                <p className="ln-body">{note.body}</p>
              )}
              {mine && !editing ? (
                <div className="ln-tools">
                  <button type="button" className="ln-btn" aria-pressed={!!note.pinned} disabled={saving} onClick={() => togglePin(note)}>{note.pinned ? 'Unpin' : 'Pin'}</button>
                  <button type="button" className="ln-btn" disabled={saving} onClick={() => { setEditingId(note.id); setEditDraft(note.body || ''); }}>Edit</button>
                  <button type="button" className="ln-btn ln-btn--danger" disabled={saving} onClick={() => remove(note)}>Delete</button>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function ActivityTimeline({ api, leadId, timezone, onOpenCall }) {
  const [serverZone, setServerZone] = useState('');
  const tz = safeZone(timezone || serverZone);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const lead = leadId != null && String(leadId).trim() !== '' ? String(leadId).trim() : '';

  useEffect(() => {
    if (!lead || typeof api !== 'function') return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDone(false);
    setItems([]);
    api('GET', '/api/leads/' + encodeURIComponent(lead) + '/timeline?limit=30')
      .then((result) => {
        if (cancelled) return;
        const next = (result && result.items) || [];
        setItems(next);
        setDone(next.length < 30);
        if (result && result.timezone) setServerZone(result.timezone);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Could not load activity.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [lead]);

  async function loadMore() {
    const last = items[items.length - 1];
    const cursor = last && last.meta && last.meta.cursor;
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const result = await api('GET', '/api/leads/' + encodeURIComponent(lead) + '/timeline?limit=30&before=' + encodeURIComponent(cursor));
      const next = (result && result.items) || [];
      setItems((prev) => prev.concat(next));
      if (next.length < 30) setDone(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not load more activity.'));
    } finally {
      setLoadingMore(false);
    }
  }

  let lastDay = '';
  return (
    <section className="tl" aria-label="Activity">
      {error ? <div className="tl-error" role="alert">{error}</div> : null}
      {loading ? <p className="tl-empty">Loading activity…</p> : null}
      {!loading && !items.length ? <p className="tl-empty">Nothing has happened on this lead yet.</p> : null}
      <div className="tl-list">
        {items.map((item) => {
          const day = dayLabel(item.at, tz);
          const showDay = day && day !== lastDay;
          if (showDay) lastDay = day;
          const rel = relativeTime(item.at);
          return (
            <div key={(item.meta && item.meta.cursor) || item.type + item.at}>
              {showDay ? <div className="tl-day">{day}</div> : null}
              <article className="tl-item">
                <div className={'tl-icon tl-icon--' + item.type}><TimelineIcon type={item.type} /></div>
                <div>
                  <div className="tl-item-top">
                    <h3 className="tl-title">{item.title}</h3>
                    <time className="tl-time" dateTime={item.at}>{formatStamp(item.at, tz)}</time>
                    {rel ? <span className="tl-time">{rel}</span> : null}
                  </div>
                  {item.actor ? <div className="tl-actor">{item.actor}</div> : null}
                  {item.body ? <p className="tl-body">{item.body}</p> : null}
                  {item.type === 'call' && item.meta && item.meta.call_sid && typeof onOpenCall === 'function' ? (
                    <button type="button" className="tl-link" onClick={() => onOpenCall(item.meta.call_sid)}>Open call</button>
                  ) : null}
                </div>
              </article>
            </div>
          );
        })}
      </div>
      {!done && items.length ? (
        <button type="button" className="tl-more" disabled={loadingMore} onClick={loadMore}>{loadingMore ? 'Loading…' : 'Load more'}</button>
      ) : null}
    </section>
  );
}
